/**
 * Sales-team "how to use this page" copy for the admin dashboard's usage/health
 * pages. Centralised here so the wording lives in ONE editable place (the sales
 * team can tweak it without touching every view), and each page just renders
 * <PageHelp {...PAGE_HELP.<key>} />. See ./PageHelp.tsx for the component.
 */

export interface PageHelpContent {
  /** One or two sentences: what this page shows. */
  summary: string;
  /** What the sales team should DO with the information (bullets). */
  actions: string[];
  /** Optional gotcha / caveat worth flagging (per-month view, new tracking, etc.). */
  caveat?: string;
}

export const PAGE_HELP = {
  costReport: {
    summary:
      'Our AI spend for the selected month, broken down per stack and per organisation (in USD and ZAR).',
    actions: [
      "Use it to understand the cost behind a customer's usage in margin or pricing conversations.",
      'Spot the heaviest-cost orgs when planning upsell or plan changes.',
    ],
    caveat:
      'Costs are estimated from token usage and can differ slightly from the actual provider invoice (settled at month-end).',
  },
  usageHealth: {
    summary:
      "How much of each org's monthly run allowance they've used — so you can spot orgs about to hit their cap. One run is roughly one tool generation (~200k tokens).",
    actions: [
      "Reach out to 'Running low' and 'Critical' orgs before they hit the cap — an upsell or top-up conversation.",
      "Reassure 'Healthy' orgs; treat 'Out of runs' as an immediate plan discussion.",
    ],
    caveat:
      "This is the selected month only — an org on 0% simply hasn't run anything yet this month. Use the Period dropdown to see earlier months.",
  },
  perUserUsage: {
    summary:
      'Run consumption per individual user, ranked highest-first — the power users driving adoption inside each brokerage.',
    actions: [
      'Identify champions to feature in case studies and references.',
      'Spot orgs where only one person is active — an opening to get the rest of the team on.',
    ],
    caveat:
      "Shared-pool orgs have no personal cap, so per-user Allocated/Remaining shows as 'unknown' — that's expected.",
  },
  mostUsedTools: {
    summary: 'Which tools brokers actually run for the selected month, ranked by usage.',
    actions: [
      'Lead with the most-used tools in demos and pitches.',
      "Spot tools that aren't landing — a training or awareness opportunity.",
    ],
  },
  brokerAdoption: {
    summary:
      'How many organisations have the Broker AI (Titan) models enabled — across BOTH the Broker AI and Intelligent Automation stacks — plus which models and how many runs this month.',
    actions: [
      'Track Broker AI reach against the Intelligent Automation base — the "are we at IAM size yet?" number.',
      'Spot orgs with the models enabled but zero runs — an activation / training follow-up.',
    ],
    caveat:
      'Counts ENABLEMENT (the model is switched on for the org), not necessarily active usage. Active organisations only; the Runs column is the selected month.',
  },
  inactiveUsers: {
    summary:
      "Users who haven't run a tool recently — your churn-risk and onboarding-follow-up list.",
    actions: [
      "Work the ≥7-day tab as 'nudge them this week'; ≥30 days are at real risk of lapsing.",
      "'Never run a job' means they signed up but never got started — prime onboarding follow-ups.",
      'Search an organisation, then Excel / PDF to send that client their own list — the export covers every matching user, not just the rows on screen.',
    ],
    caveat:
      "A long list is normal while many invited seats haven't activated. 'Last login' is still filling in (login tracking is new) — judge activity by 'Last run'.",
  },
  leaderboard: {
    summary: 'The most-active users and organisations for the selected date range, by runs.',
    actions: [
      'Nurture the top users and orgs — your renewal, reference and upsell shortlist.',
      "Change the date range (e.g. Today) or filter to one organisation to see who's on top.",
    ],
  },
  conversionFunnel: {
    summary:
      'How users progress from sign-up to paying: registered, then logged in, then ran a job, then in trial, then converted.',
    actions: [
      'See where people drop out of the journey, and chase expiring trials before they churn.',
    ],
    caveat:
      "Login tracking is brand new with no history to backfill, so 'Logged in' currently shows fewer people than 'Ran a job' — that's not an error; it lines up as logins accrue. Read the funnel from 'Registered' and 'Ran a job' for now.",
  },
  referralSources: {
    summary:
      'Where our organisations were referred from (e.g. FSP Hub vs Direct), and how each source is converting and earning — orgs, paying customers, conversion rate, monthly recurring revenue, plus runs and AI spend. Also shows the commission payable to FSP Hub for the month.',
    actions: [
      'Track how a distribution partner (e.g. FSP Hub) is performing — for commission and revenue-share conversations.',
      'Reconcile month-end commission payable to FSP Hub — the panel gives the qualifying paid orgs, the tier rate and the amount owed.',
      'Compare conversion rates by source to see which channels send the best-fitting customers.',
    ],
    caveat:
      "Orgs, paying and revenue reflect the current org list; runs and AI cost are for the selected month. 'Paying' means the org has a monthly subscription amount set; 'Unattributed' groups orgs with no source tag yet. Commission is stricter — it counts only FSP-Hub orgs whose subscription is active and paid, and the rate steps with that count (10% → 25%).",
  },
  feedback: {
    summary:
      "Everything users tell us, in two tabs. 'Ideas & Feedback' is what they submit from the in-app Feedback & Ideas button — improvements, new tool ideas and bug reports — which you triage through New, Under Review, Planned, In Development, Completed or Not Proceeding. 'Trial Reviews' is the end-of-trial star rating plus what they found most useful and what they'd like improved.",
    actions: [
      'Work the Awaiting-triage count down: open a submission, read the detail, and set its status so the submitter can be told where it landed.',
      'Read what trial users actually say to guide the roadmap and the sales pitch.',
      'Follow up with low-rated reviewers, and quote happy ones in case studies and references.',
    ],
    caveat:
      'Both are optional, so not everyone leaves one. The notification bell pings you when a new submission or review arrives. Statuses are internal — the submitter does not see them.',
  },
} satisfies Record<string, PageHelpContent>;
